import cx from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Message } from '@bwoty-web/ui-kit';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import { bindActionCreators } from 'redux';
import { useEffect, useState } from 'react';

import PaxField from '../components/pax/PaxField';
import PaxSelect from '../components/pax/PaxSelect';
import QueryKeys from '../utils/url/queryKeys';
import DateField from '../components/date/DateField';
import DatePicker from '../components/date/DatePicker';
import bookingTypes from '../constants/bookingTypes';
import SelectOverlay from '../components/selectOverlay/SelectOverlay';
import DepartureField from '../components/departure/DepartureField';
import DepartureSelect from '../components/departure/DepartureSelect';
import TdmSearchPlaceholder from '../components/placeholders/tdmSearch';
import { maxNumberOfPax } from '../constants/pax';
import { getPaxFormText, getRoomDistributionString } from '../utils/pax/helper';
import { shiftFocusToPreviousSiblingIfKeyBoardWasUsed } from '../utils/keyboardHandler';

import * as TdmSearchActions from '../redux/actions/tdmSearch/tdmSearch';

import './tdmSearch.scss';
import './bookingstart.scss'; // Needed for shared styles

const TdmSearch = ({
  actions,
  labels,
  isLoading,
  departures,
  selectedDeparture,
  selectedDepartureDate,
  roomDistribution,
  siteSettings,
  searchParams,
}) => {
  const { isCruise, isInspiration, departureDate } = searchParams;

  const [isCompressed, setIsCompressed] = useState(!isInspiration && departureDate);
  const [showDepartureLayer, setShowDepartureLayer] = useState(false);
  const [showDateLayer, setShowDateLayer] = useState(false);
  const [showPaxLayer, setShowPaxLayer] = useState(false);
  const [paxIsValid, setPaxIsValid] = useState(true);

  useEffect(() => {
    actions.init(siteSettings, searchParams);
  }, [actions]);

  const isSelectOverlayOpen = () => {
    return showDepartureLayer || showPaxLayer || showDateLayer;
  };

  const { datePicker } = siteSettings;

  const isGlobetrotter = useIsGlobetrotter();

  if (!labels || isLoading) {
    return <TdmSearchPlaceholder />;
  }

  const {
    changeButtonLabel,
    compressedHeading,
    departureInputLabel,
    inspirationHeading,
    noDepartureMessageHeader,
    searchButtonText,
  } = labels;

  if (!departures || !departures?.length) {
    return <Message header={noDepartureMessageHeader} messageType="alert" />;
  }

  const setDepartureDate = (day) => {
    actions.changeDepartureDate(day);
    setShowDateLayer(false);
  };

  const setCruiseRoomDistribution = (e, updatedRoomDistribution) => {
    actions.changeRoomDistribution(updatedRoomDistribution, 1, false);
    shiftFocusToPreviousSiblingIfKeyBoardWasUsed(e);
    setShowPaxLayer(false);
  };

  const setStopOverRoomDistribution = (e, updatedRoomDistribution, selectedNumberOfRooms) => {
    actions.changeRoomDistribution(updatedRoomDistribution, selectedNumberOfRooms);
    shiftFocusToPreviousSiblingIfKeyBoardWasUsed(e);
    setShowPaxLayer(false);
  };

  const getSearchUrl = () => {
    const formattedDepartureDate = moment(selectedDepartureDate, 'YYYY-MM-DD').format('YYYYMMDD');

    const searchQueryParams = new URLSearchParams(window?.location?.search);
    searchQueryParams.set(QueryKeys.TDM_SEARCH_CONTROL.departureId, selectedDeparture.itemId);
    searchQueryParams.set(QueryKeys.TDM_SEARCH_CONTROL.departureDate, formattedDepartureDate);
    searchQueryParams.set(
      QueryKeys.TDM_SEARCH_CONTROL.roomDistribution,
      getRoomDistributionString(roomDistribution, bookingTypes.dp),
    );
    searchQueryParams.delete(QueryKeys.TDM_SEARCH_CONTROL.sessionId);
    searchQueryParams.delete(QueryKeys.TDM_SEARCH_CONTROL.price);

    const searchUrl = `${
      window.location.pathname
    }?${searchQueryParams.toString()}#tdm-search-app-root`;
    return searchUrl;
  };

  const searchTdm = () => {
    const url = getSearchUrl();
    if (window.location.pathname + window.location.search === url) {
      window.location.reload();
    } else {
      window.location.href = url;
    }
  };

  const getDepartureFieldAndModal = () => {
    return (
      <DepartureField
        className="tdm-search__departure-field"
        isActive={showDepartureLayer}
        isOtherFieldActive={!showDepartureLayer && isSelectOverlayOpen()}
        labels={labels}
        selectedAirport={selectedDeparture}
        toggleOverlay={() => setShowDepartureLayer(!showDepartureLayer)}
      >
        {!isLoading && (
          <SelectOverlay heading={departureInputLabel} close={() => setShowDepartureLayer(false)}>
            <DepartureSelect
              airportList={departures}
              changeAirport={(e, departure) => {
                shiftFocusToPreviousSiblingIfKeyBoardWasUsed(e);
                setShowDepartureLayer(false);
                actions.changeDepartureAirport(departure);
              }}
              heading={departureInputLabel}
              selectedAirport={selectedDeparture}
            />
          </SelectOverlay>
        )}
      </DepartureField>
    );
  };

  const getDateFieldAndModal = () => {
    const minDate = isCruise
      ? moment().add(datePicker.cruiseBookDaysAhead, 'days')
      : moment().add(datePicker.dpBookDaysAhead, 'days');

    const maxDate = isCruise
      ? moment().add(datePicker.cruiseMaxDaysToDeparture, 'days')
      : moment().add(datePicker.dpMaxDaysToReturn, 'days');

    return (
      <DateField
        className="tdm-search__date-field"
        datePickerIsActive={showDateLayer}
        dateSettings={datePicker}
        hideReturnDate
        isActive={showDateLayer}
        isLoading={isLoading}
        isOtherFieldActive={!showDateLayer && isSelectOverlayOpen()}
        labels={labels}
        selectedDepartureDate={selectedDepartureDate}
        toggleOverlay={() => setShowDateLayer(!showDateLayer)}
      >
        {!isLoading && showDateLayer && (
          <DatePicker
            changeDate={setDepartureDate}
            close={() => setShowDateLayer(false)}
            dateSettings={datePicker}
            isHidden={!showDateLayer}
            labels={labels}
            onlySelectableDates
            range={{ min: minDate, max: maxDate }}
            selectableDepartureDates={selectedDeparture.departureDates}
            selectedDepartureDate={selectedDepartureDate}
          />
        )}
      </DateField>
    );
  };

  const getPaxFieldAndModal = () => {
    return (
      <PaxField
        className="tdm-search__pax-field"
        isActive={showPaxLayer}
        isLoading={isLoading}
        isOtherFieldActive={!showPaxLayer && isSelectOverlayOpen()}
        labels={labels}
        roomDistribution={roomDistribution}
        toggleOverlay={() => setShowPaxLayer(!showPaxLayer)}
      >
        {!isLoading && showPaxLayer && (
          <PaxSelect
            activeValidation={!paxIsValid}
            changeRoomDistribution={
              isCruise ? setCruiseRoomDistribution : setStopOverRoomDistribution
            }
            close={() => setShowPaxLayer(false)}
            hideSameRoomCheckbox={isCruise}
            isCruise={isCruise}
            labels={labels}
            maxNumberOfPax={isCruise ? maxNumberOfPax.cruise : maxNumberOfPax.dp}
            multipleRooms={isCruise}
            numberOfRooms={isCruise ? 1 : roomDistribution.length}
            roomDistribution={roomDistribution}
          />
        )}
      </PaxField>
    );
  };

  const getFormattedDate = (date) => {
    return `${date.substring(0, 4)}-${date.substring(4, 6)}-${date.substring(6)}`;
  };

  const classNames = cx(
    'bookingstart',
    'tdm-search',
    { 'tdm-search--compressed': isCompressed },
  );

  if (isCompressed) {
    return (
      <div className={classNames}>
        <div className="tdm-search__compressed-main">
          <div className="tdm-search__compressed-heading">{compressedHeading}</div>
          <div className="tdm-search__travel-info">
            <span>{selectedDeparture.name},&nbsp;</span>
            <span>{getFormattedDate(selectedDepartureDate)},&nbsp;</span>
            <span>{getPaxFormText(roomDistribution, labels)}</span>
          </div>
        </div>
        <Button
          variant="ghost"
          className="tdm-search__expand-button"
          onClick={() => setIsCompressed(false)}
        >
          {changeButtonLabel}
        </Button>
      </div>
    );
  }

  return (
    <div className={classNames}>
      {isInspiration && <div className="tdm-search__inspiration-heading">{inspirationHeading}</div>}
      <div className="tdm-search__search-container">
        {getDepartureFieldAndModal()}
        {getDateFieldAndModal()}
        {getPaxFieldAndModal()}
        <div className="tdm-search__button-container">
          <Button
            variant="primary"
            disabled={isLoading}
            onClick={searchTdm}
            rel="nofollow"
            className="tdm-search__search-button"
          >
            {searchButtonText}
          </Button>
        </div>
      </div>
    </div>
  );
};

TdmSearch.propTypes = {
  actions: PropTypes.shape().isRequired,
  departures: PropTypes.arrayOf(PropTypes.shape({})),
  isLoading: PropTypes.bool,
  labels: PropTypes.shape(),
  roomDistribution: PropTypes.arrayOf(PropTypes.shape()),
  searchParams: PropTypes.shape().isRequired,
  selectedDeparture: PropTypes.shape(),
  selectedDepartureDate: PropTypes.string,
  siteSettings: PropTypes.shape().isRequired,
};

TdmSearch.defaultProps = {
  departures: undefined,
  isLoading: undefined,
  labels: undefined,
  roomDistribution: undefined,
  selectedDeparture: undefined,
  selectedDepartureDate: undefined,
};

const mapActions = (dispatch) => {
  return {
    actions: bindActionCreators({ ...TdmSearchActions }, dispatch),
  };
};

const mapStateToProps = (state) => {
  return {
    isLoading: state.isLoading,
    labels: state.labels,
    departures: state.departures,
    selectedDeparture: state.selectedDeparture,
    selectedDepartureDate: state.selectedDepartureDate,
    roomDistribution: state.roomDistribution,
    siteSettings: state.siteSettings,
    searchParams: state.searchParams,
  };
};

export default connect(mapStateToProps, mapActions)(TdmSearch);
